<template>
    <section class="space-y-3 rounded-lg border border-slate-200 p-4" aria-label="Companies blocked in this campaign">
        <h4 class="text-sm font-semibold text-slate-900">Companies blocked in this campaign</h4>
        <p class="text-xs text-slate-600">Block exact company or email domains here, even when company exceptions above are allowed. Other campaigns and organization exclusions are unchanged. Subdomains are not included automatically.</p>
        <p v-if="error" role="alert" class="text-sm text-red-700">{{ error }}</p>
        <p v-if="notice" role="status" class="text-sm text-slate-700">{{ notice }}</p>
        <p v-if="uncertain" class="text-xs text-amber-800">The previous request may have saved. Reload saved rules before making another change.</p>
        <div class="flex flex-wrap gap-2">
            <UButton size="xs" color="neutral" variant="outline" :loading="loading" :disabled="saving || disabled" @click="refresh">{{ uncertain ? 'Reload saved rules' : 'Refresh status' }}</UButton>
            <UButton v-if="state?.excludedCompanyDomains.length" size="xs" color="neutral" variant="outline" :loading="saving" :disabled="busy || uncertain" @click="save('retry', [])">Retry removal</UButton>
        </div>
        <template v-if="state">
            <div class="space-y-1 text-xs text-slate-600" role="status">
                <p>{{ state.remainingAssignmentCount }} matching campaign assignments remain locally.</p>
                <p v-if="state.unsupportedSmartleadCount" class="text-amber-800">{{ state.unsupportedSmartleadCount }} already-sequenced Smartlead assignments cannot be removed automatically. Provider follow-up is required.</p>
                <p v-if="state.missingBisonIdentifierCount" class="text-amber-800">{{ state.missingBisonIdentifierCount }} already-sequenced Email Bison assignments have missing or invalid provider campaign or lead IDs. Provider follow-up is required.</p>
                <p>Removal happens in the background. A queued request or zero local matches does not confirm that the provider has stopped sending.</p>
            </div>
            <ul v-if="state.excludedCompanyDomains.length" class="max-h-48 space-y-2 overflow-y-auto">
                <li v-for="domain in state.excludedCompanyDomains" :key="domain" class="flex items-center justify-between gap-2 text-sm">
                    <span class="break-all">{{ domain }}</span>
                    <UButton size="xs" color="neutral" variant="ghost" :disabled="busy || uncertain" @click="pending = { action: 'remove', domain }">Remove block</UButton>
                </li>
            </ul>
            <p v-else class="text-xs text-slate-500">No companies are blocked by this campaign's domain list.</p>
            <form class="flex gap-2" @submit.prevent="reviewAdd">
                <UInput v-model="domainInput" placeholder="example.com" aria-label="Company domain to block" class="min-w-0 flex-1" :disabled="busy || uncertain" :maxlength="2048" />
                <UButton type="submit" size="sm" :disabled="busy || uncertain || !domainInput.trim()">Block company</UButton>
            </form>
            <div v-if="pending" class="space-y-3 rounded border border-amber-200 bg-amber-50 p-3">
                <p class="break-all text-sm font-medium">{{ pending.action === 'add' ? 'Block' : 'Remove block for' }} {{ pending.domain }} in this campaign only?</p>
                <p class="text-xs text-slate-700">{{ pending.action === 'add'
                    ? 'This prevents future matching assignments and requests background removal of existing matching outreach. It does not block this company in other campaigns. It does not stop provider sending instantly.'
                    : 'This permits future matching contacts if all other rules pass. It never restores removed assignments or clears individual-contact or organization exclusions.' }}</p>
                <div class="flex gap-2">
                    <UButton size="xs" :loading="saving" :disabled="busy || uncertain" @click="save(pending.action, [pending.domain])">Confirm {{ pending.action === 'add' ? 'block' : 'remove block' }}</UButton>
                    <UButton size="xs" color="neutral" variant="ghost" :disabled="saving" @click="pending = null">Cancel</UButton>
                </div>
            </div>
        </template>
    </section>
</template>

<script setup lang="ts">
import type { CampaignCompanyExclusionsStatus, SaveCampaignCompanyExclusionsResult } from '@coldreach/campaigns';

const props = defineProps<{ campaignId: number; disabled?: boolean }>();
const emit = defineEmits<{ updated: []; busy: [value: boolean] }>();
const state = ref<CampaignCompanyExclusionsStatus | null>(null);
const domainInput = ref('');
const pending = ref<{ action: 'add' | 'remove'; domain: string } | null>(null);
const loading = ref(false);
const saving = ref(false);
const uncertain = ref(false);
const error = ref('');
const notice = ref('');
const busy = computed(() => loading.value || saving.value || !!props.disabled);
function errorMessage(cause: unknown): string {
    const detail = cause as { data?: { message?: string }; message?: string };
    return detail?.data?.message || detail?.message || 'Request failed. Reload saved rules.';
}
function reviewAdd() {
    pending.value = { action: 'add', domain: domainInput.value.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split(/[/?#]/)[0]! };
}
async function refresh() {
    loading.value = true;
    error.value = '';
    pending.value = null;
    try {
        state.value = await $fetch<CampaignCompanyExclusionsStatus>(`/api/icps/${props.campaignId}/company-exclusions`);
        uncertain.value = false;
    } catch (cause) {
        error.value = errorMessage(cause);
        uncertain.value = true;
    } finally { loading.value = false; }
}
async function save(action: 'add' | 'remove' | 'retry', domains: string[]) {
    if (!state.value || busy.value || uncertain.value) return;
    saving.value = true;
    emit('busy', true);
    error.value = '';
    notice.value = '';
    try {
        const result = await $fetch<SaveCampaignCompanyExclusionsResult>(`/api/icps/${props.campaignId}/company-exclusions`, {
            method: 'POST', retry: 0,
            body: { confirmed: true, action, domains, expectedConfigurationRevision: state.value.configurationRevision },
        });
        notice.value = result.outcome === 'saved' ? 'Campaign company blocks saved.' : 'Saved company blocks are unchanged.';
        if (result.withdrawal.status === 'requested') notice.value += ' Background removal requested, not confirmed complete.';
        if (result.withdrawal.status === 'failed') notice.value += ` ${result.withdrawal.error}`;
        if (action === 'add') domainInput.value = '';
        pending.value = null;
        // This GET observes current remaining assignments; the save receipt never invents completion.
        await refresh();
    } catch (cause) {
        error.value = errorMessage(cause);
        uncertain.value = true;
    } finally {
        saving.value = false;
        emit('busy', false);
        // Even an uncertain POST may have committed. Reload the sibling policy revision.
        emit('updated');
    }
}
onMounted(refresh);
watch(() => props.campaignId, () => { state.value = null; notice.value = ''; void refresh(); });
</script>
